import React, { memo } from 'react'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import Paper from '@material-ui/core/Paper'
import Chip from '@material-ui/core/Chip'
import MenuItem from '@material-ui/core/MenuItem'
import CancelIcon from '@material-ui/icons/Cancel'
import AsyncSelect from 'react-select/async'
import Select from 'react-select'

import classNames from 'classnames'
import styles from './styles'

const useStyles = makeStyles(styles)

const NoOptionsMessage: (props: any) => JSX.Element = props => (
	<Typography
		color="textSecondary"
		className={props.selectProps.classes.noOptionsMessage}
		{...props.innerProps}
	>
		{props.children}
	</Typography>
)

const inputComponent: (props: any) => JSX.Element = ({
	inputRef,
	...props
}) => <div ref={inputRef} {...props} />

const Control: (props: any) => JSX.Element = ({
	children,
	innerProps,
	innerRef,
	selectProps: { classes, TextFieldProps },
}) => (
	<TextField
		fullWidth
		InputProps={{
			inputComponent,
			inputProps: {
				className: classes.input,
				ref: innerRef,
				children,
				...innerProps,
			},
		}}
		{...TextFieldProps}
	/>
)

const Option: (props: any) => JSX.Element = props => (
	<MenuItem
		ref={props.innerRef}
		selected={props.isFocused}
		component="div"
		style={{
			fontWeight: props.isSelected ? 500 : 400,
		}}
		{...props.innerProps}
	>
		{props.children}
	</MenuItem>
)

const Placeholder: (props: any) => JSX.Element = ({
	selectProps,
	innerProps = {},
	children,
}) => (
	<Typography
		color="textSecondary"
		className={selectProps.classes.placeholder}
		{...innerProps}
	>
		{children}
	</Typography>
)

const ValueContainer: (props: any) => JSX.Element = props => (
	<div className={props.selectProps.classes.valueContainer}>
		{props.children}
	</div>
)
const SingleValue: (props: any) => JSX.Element = props => {
	return (
		<Typography
			className={props.selectProps.classes.singleValue}
			{...props.innerProps}
		>
			{props.children}
		</Typography>
	)
}
const MultiValue: (props: any) => JSX.Element = props => (
	<Chip
		tabIndex={-1}
		label={props.children}
		className={classNames(props.selectProps.classes.chip, {
			[props.selectProps.classes.chipFocused]: props.isFocused,
		})}
		onDelete={props.removeProps.onClick}
		deleteIcon={<CancelIcon {...props.removeProps} />}
	/>
)

const Menu: (props: any) => JSX.Element = props => (
	<Paper
		square
		className={props.selectProps.classes.paper}
		{...props.innerProps}
	>
		{props.children}
	</Paper>
)

const components = {
	Control,
	Menu,
	MultiValue,
	NoOptionsMessage,
	Option,
	Placeholder,
	ValueContainer,
	SingleValue,
}

const SearchMultiSelect: React.FunctionComponent<any> = ({
	loadOptions,
	handleChange,
	items,
	singleValue,
	isMulti,
	placeholderText,
	defaultOptions,
	options,
	isAsyncSelect = true,
	defaultValue,
	value,
}) => {
	const theme = useTheme()
	const classes = useStyles()
	const isMobile = useMediaQuery('@media (max-width:680px)')

	const selectStyles = {
		input: (base: any) => ({
			...base,
			color: theme.palette.text.primary,
			'& input': {
				font: 'inherit',
			},
		}),
		dropdownIndicator: (base: any) => ({
			...base,
			cursor: 'default',
		}),
	}

	return (
		<Grid container={true} direction="row" style={{ position: 'relative' }}>
			<Grid
				container={true}
				direction="row"
				className={
					isMobile
						? classes.skillsOuterContainerMobile
						: classes.skillsOuterContainer
				}
			>
				<Grid
					container={true}
					direction="row"
					className={
						isMobile
							? classes.skillsInnerContainerMobile
							: classes.skillsInnerContainer
					}
				></Grid>
				<Grid container={true} direction="row">
					{isAsyncSelect ? (
						<AsyncSelect
							cacheOptions
							loadOptions={loadOptions}
							defaultOptions={defaultOptions}
							className={classes.skillsSelect}
							classes={classes}
							styles={selectStyles}
							inputId="react-select-multiple"
							TextFieldProps={{
								label: null,
								InputLabelProps: {
									htmlFor: 'react-select-multiple',
									shrink: true,
								},
							}}
							placeholder={placeholderText}
							components={components}
							value={isMulti ? items : singleValue}
							onChange={handleChange}
							isMulti={isMulti}
							isClearable={true}
							backspaceRemovesValue={true}
						/>
					) : (
						<Select
							inputId="react-select-simple"
							TextFieldProps={{
								label: null,
								InputLabelProps: {
									htmlFor: 'react-select-simple',
									shrink: true,
								},
							}}
							components={components}
							isMulti={false}
							options={options}
							classes={classes}
							styles={selectStyles}
							className={classes.skillsSelect}
							value={value}
							onChange={handleChange}
						/>
					)}
				</Grid>
			</Grid>
		</Grid>
	)
}

export default memo(SearchMultiSelect)
